// Formatting and severity for the situation report. Pure.
//
// Every row links through to the existing admin page that PROVES it. That is
// what makes the agent's rationale checkable rather than merely readable — the
// difference between "the agent says seven gifts are unthanked" and "here are
// the seven donations, click one".

import type { Tone } from '../../theme/listkit'
import type { CmSignals } from './types'

export interface SignalFamily {
  key: keyof CmSignals
  title: string
  /** What the number means, in the owner's terms. */
  blurb: string
  /** Where a row goes when clicked. `profileIds` maps user_id → profiles.id (see profileHref). */
  link: (row: Record<string, unknown>, profileIds: ReadonlyMap<string, string>) => string | null
  count: (s: CmSignals) => number
  severity: (n: number) => Tone
}

export const SIGNAL_FAMILIES: SignalFamily[] = [
  {
    key: 'unthanked_gifts',
    title: 'Gifts with no thank-you',
    blurb: 'A member gave and nothing landed in their inbox. This is the clearest dropped ball the app has.',
    link: () => '/donations',
    count: (s) => s.unthanked_gifts?.length ?? 0,
    severity: (n) => (n === 0 ? 'green' : n < 3 ? 'gold' : 'red'),
  },
  {
    key: 'unanswered_mentorship',
    title: 'Mentorship requests waiting',
    blurb: 'Someone asked for a mentor. An open request has nobody on the hook and needs you to route it.',
    link: () => '/mentorship_requests',
    count: (s) => s.unanswered_mentorship?.length ?? 0,
    severity: (n) => (n === 0 ? 'green' : n < 3 ? 'gold' : 'red'),
  },
  {
    key: 'rsvp_gaps',
    title: 'RSVPs still open',
    blurb: 'An event is close and these members have not confirmed.',
    link: () => '/rsvps',
    count: (s) => s.rsvp_gaps?.length ?? 0,
    severity: (n) => (n === 0 ? 'green' : 'gold'),
  },
  {
    key: 'at_risk',
    title: 'Members who have not come back',
    blurb:
      'The app cannot reach these members — they do not open it. Bringing them back takes a call or a text from you.',
    link: (r, profileIds) => profileHref(r.user_id, profileIds),
    count: (s) => s.at_risk?.length ?? 0,
    severity: (n) => (n === 0 ? 'green' : n < 5 ? 'gold' : 'red'),
  },
  {
    key: 'profile_gaps',
    title: 'Incomplete profiles',
    blurb: 'Empty avatars and missing cities are why the directory and the map look thin.',
    link: (r, profileIds) => profileHref(r.user_id, profileIds),
    count: (s) => s.profile_gaps?.length ?? 0,
    severity: (n) => (n === 0 ? 'green' : 'neutral'),
  },
  {
    key: 'upcoming_events',
    title: 'Upcoming events',
    blurb: 'What the community has to look forward to, and how many have said yes.',
    link: () => '/events',
    count: (s) => s.upcoming_events?.length ?? 0,
    severity: () => 'neutral',
  },
]

/**
 * A member's admin profile page, or the member list when we cannot name one.
 *
 * /profiles/show/:id resolves profiles.id, but signal rows (and a proposal's
 * evidence) carry user_id — the auth id, which is profiles.user_id. The two are
 * different values in every row, so a link built from user_id opens an empty
 * profile. The map comes from useProfileIds(); until it has the row, the list.
 */
export function profileHref(userId: unknown, profileIds: ReadonlyMap<string, string>): string {
  const id = typeof userId === 'string' ? profileIds.get(userId) : undefined
  return id ? `/profiles/show/${id}` : '/profiles'
}

/**
 * Days since a member last contributed to the feed.
 *
 * Named carefully: the global feed lives in GetStream, not Postgres, so this
 * measures member-authored artifacts (posts and photos) rather than everything
 * that appears. It is the supply side, which is the thing worth acting on.
 */
export function feedSilenceLabel(s: CmSignals): string {
  const f = s.feed_silence
  if (!f || f.days_silent == null) return 'No member post on record'
  const d = Math.round(f.days_silent)
  if (d === 0) return 'A member posted today'
  if (d === 1) return 'A member last posted yesterday'
  return `${d} days since a member last posted`
}

export function feedSilenceTone(s: CmSignals): Tone {
  const d = s.feed_silence?.days_silent
  if (d == null) return 'red'
  if (d <= 3) return 'green'
  if (d <= 14) return 'gold'
  return 'red'
}
